import CustomButton from '@/components/atoms/CustomButton';
import useToast from '@/hooks/useToast';
import { CloseIcon } from '@chakra-ui/icons';
import { Box, HStack, IconButton, Input, Text, VStack } from '@chakra-ui/react';
import React, { useRef } from 'react';
import { FieldError } from './FormBits';
import { TVideoSource } from '../types';

const MAX_VIDEO_SIZE_MB = 500;
const ACCEPTED_VIDEO = 'video/mp4,video/quicktime,video/webm';

type Props = {
  source: TVideoSource;
  onSourceChange: (source: TVideoSource) => void;
  file: File | null;
  onFileChange: (file: File | null) => void;
  youtubeUrl: string;
  onYoutubeUrlChange: (url: string) => void;
  // Persisted Mux status when editing (e.g. a video already uploaded).
  existingMuxStatus?: string | null;
  error?: string;
};

const ToggleButton = ({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) => (
  <Box
    as="button"
    onClick={onClick}
    flex={1}
    paddingY="8px"
    fontSize="13px"
    fontWeight={700}
    textTransform="uppercase"
    letterSpacing="0.3px"
    borderRadius="8px"
    color={active ? 'extra.white' : 'text.mediumBlue'}
    backgroundColor={active ? 'primary.500' : 'transparent'}
    transition="all 0.15s ease"
  >
    {label}
  </Box>
);

const VideoSourceField = ({
  source,
  onSourceChange,
  file,
  onFileChange,
  youtubeUrl,
  onYoutubeUrlChange,
  existingMuxStatus,
  error,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const handlePick = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0];
    if (!picked) return;
    if (picked.size > MAX_VIDEO_SIZE_MB * 1024 * 1024) {
      toast({
        status: 'warning',
        title: 'File too large.',
        description: `Video must be under ${MAX_VIDEO_SIZE_MB}MB.`,
      });
      return;
    }
    onFileChange(picked);
  };

  const clearFile = () => {
    onFileChange(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <VStack align="stretch" gap="8px" width="100%">
      {/* Segmented toggle */}
      <HStack
        padding="3px"
        borderRadius="10px"
        border="1px solid"
        borderColor="background.separatorBlue"
        backgroundColor="white"
        gap="3px"
      >
        <ToggleButton
          label="Upload File"
          active={source === 'mux'}
          onClick={() => onSourceChange('mux')}
        />
        <ToggleButton
          label="YouTube Link"
          active={source === 'youtube'}
          onClick={() => onSourceChange('youtube')}
        />
      </HStack>

      {source === 'mux' ? (
        <VStack align="stretch" gap="6px">
          <CustomButton
            label="Upload Reel File"
            backgroundColor="primary.500"
            width="100%"
            onClick={() => inputRef.current?.click()}
          />
          <Text fontSize="12px" color="text.mediumGray">
            MP4, MOV, WebM up to {MAX_VIDEO_SIZE_MB} MB
          </Text>
          {file && (
            <HStack
              justify="space-between"
              border="1px solid"
              borderColor="background.separatorBlue"
              borderRadius="8px"
              paddingX="12px"
              paddingY="8px"
              backgroundColor="white"
            >
              <Text fontSize="13px" color="text.darkBlue" noOfLines={1}>
                {file.name}
              </Text>
              <IconButton
                aria-label="Remove file"
                icon={<CloseIcon boxSize={2.5} />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={clearFile}
              />
            </HStack>
          )}
          {!file && existingMuxStatus && (
            <Text fontSize="12px" color="text.mediumBlue">
              Current video status: {existingMuxStatus}. Upload a new file to replace it.
            </Text>
          )}
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_VIDEO}
            hidden
            onChange={handlePick}
          />
        </VStack>
      ) : (
        <Input
          backgroundColor="white"
          placeholder="YouTube link e.g. youtube.com/watch?v=..."
          value={youtubeUrl}
          onChange={(e) => onYoutubeUrlChange(e.target.value)}
        />
      )}

      <FieldError error={error} />
    </VStack>
  );
};

export default VideoSourceField;
